import React, { useState, useMemo } from 'react';
import './EntryHistoryTable.css';

const EntryHistoryTable = ({ entries, onEdit, type, onTimeoutUpdate, showActions = true, readOnly = false, showCreatedBy = false, supervisorActions = {}, onMarkAction }) => {
  console.log('EntryHistoryTable entries:', entries); // <-- Debug log

  const user = JSON.parse(localStorage.getItem('user'));
  if (showCreatedBy && user?.role === 'User') {
    console.warn('showCreatedBy is true for User role. This should not happen.');
  }

  const formatTimeToLocal = (timeStr) => {
    if (!timeStr) return '-';
    const today = new Date().toISOString().split('T')[0];
    const dateTime = new Date(`${today}T${timeStr}`);
    return dateTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    transport_mode: '',
    entry_type: '',
    purpose: '', // <-- add purpose filter
    date_from: '',
    date_to: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      // Status filter
      if (filters.status !== 'all') {
        const entryStatus = entry.time_out ? 'completed' : 'pending';
        if (entryStatus !== filters.status) return false;
      }

      // Transport mode filter (was vehicle_type)
      if (filters.transport_mode && entry.transport_mode !== filters.transport_mode) {
        return false;
      }

      // Entry type filter
      if (filters.entry_type && entry.entry_type !== filters.entry_type) {
        return false;
      }

      // Purpose filter (only for outward)
      if (type === 'outward' && filters.purpose && entry.purpose !== filters.purpose) {
        return false;
      }

      // Date range filter
      if (filters.date_from || filters.date_to) {
        const entryDate = new Date(entry.entry_date);
        if (filters.date_from && entryDate < new Date(filters.date_from)) return false;
        if (filters.date_to && entryDate > new Date(filters.date_to)) return false;
      }

      // Global search
      if (searchTerm) {
        const searchValue = searchTerm.toLowerCase();
        const searchableValues = Object.entries(entry).reduce((acc, [key, value]) => {
          if (typeof value === 'string' || typeof value === 'number') {
            acc.push(String(value).toLowerCase());
          }
          return acc;
        }, []);

        // Add materials search
        if (entry.materials_list) {
          entry.materials_list.forEach(material => {
            searchableValues.push(
              material.name?.toLowerCase(),
              material.quantity?.toString().toLowerCase(),
              material.uom?.toLowerCase()
            );
          });
        }

        return searchableValues.some(value => value?.includes(searchValue));
      }

      return true;
    });
  }, [entries, searchTerm, filters, type]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredEntries.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredEntries.length / entriesPerPage);

  const renderPagination = () => (
    <div className="pagination">
      <button
        onClick={() => setCurrentPage(1)}
        disabled={currentPage === 1}
        className="pagination-button"
      >
        First
      </button>
      <button
        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        className="pagination-button"
      >
        Previous
      </button>
      <span className="pagination-info">
        Page {currentPage} of {totalPages} ({filteredEntries.length} entries)
      </span>
      <button
        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
        className="pagination-button"
      >
        Next
      </button>
      <button
        onClick={() => setCurrentPage(totalPages)}
        disabled={currentPage === totalPages}
        className="pagination-button"
      >
        Last
      </button>
    </div>
  );

  const getEntryStatus = (entry) => {
    // If supervisor_action exists, show as 'mistake'
    const hasSupervisorAction = entry.supervisor_action && (
      (Array.isArray(entry.supervisor_action) && entry.supervisor_action.length > 0) ||
      (!Array.isArray(entry.supervisor_action) && typeof entry.supervisor_action === 'object')
    );
    let status;
    if (hasSupervisorAction) {
      status = 'mistake';
    } else {
      status = entry.time_out ? 'completed' : 'pending';
    }
    return (
      <span className={`status-badge status-${status}`}>
        {status === 'mistake'
          ? 'Mistake'
          : status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const renderMaterialsCell = (materials_list) => {
    if (!materials_list || !Array.isArray(materials_list) || materials_list.length === 0) {
      return <td>-</td>;
    }

    return (
      <td className="materials-cell">
        <table className="materials-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Qty</th>
              <th>UoM</th>
            </tr>
          </thead>
          <tbody>
            {materials_list.map((material, idx) => (
              <tr key={idx}>
                <td>{material.name || '-'}</td>
                <td>{material.quantity || '-'}</td>
                <td>{material.uom || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    );
  };

  const renderTimeOutCell = (entry, isInward) => (
    <td>
      {entry.time_out ? (
        formatTimeToLocal(entry.time_out)
      ) : (
        'Pending'
      )}
    </td>
  );

  const showReferenceColumn = useMemo(() => {
    return entries.some(entry => entry.entry_type === 'Challan');
  }, [entries]);

  const getSupervisorAction = (entry) => {
    const key = `${type.charAt(0).toUpperCase() + type.slice(1)}_${entry.id}`;
    const actions = supervisorActions[key];
    if (actions && actions.length > 0) {
      return actions[0];
    }
    if (entry.supervisor_action) {
      if (Array.isArray(entry.supervisor_action) && entry.supervisor_action.length > 0) {
        return entry.supervisor_action[0];
      }
      if (typeof entry.supervisor_action === 'object') {
        return entry.supervisor_action;
      }
    }
    return null;
  };

  const renderBillsCell = (bills) => {
    if (!bills || !Array.isArray(bills) || bills.length === 0) {
      return <td>-</td>;
    }
    return (
      <td className="bills-cell">
        <table className="bills-table">
          <thead>
            <tr>
              <th>Bill No.</th>
              <th>Bill Amount</th>
            </tr>
          </thead>
          <tbody>
            {bills.map((bill, idx) => (
              <tr key={idx}>
                <td>{bill.bill_number}</td>
                <td>{bill.bill_amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    );
  };

  const renderInwardTable = () => (
    <>
      <table>
        <thead>
          <tr>
            <th>Serial No.</th>
            <th>Date</th>
            <th>Party Name</th>
            <th>Bill Details</th>
            <th>Entry Type</th>
            <th>Transport Mode</th>
            <th>Source Location</th>
            {showReferenceColumn && <th>Reference No.</th>}
            <th>PO Number</th>
            <th>Gross Weight</th>
            <th>Tare Weight</th>
            <th>Net Weight</th>
            <th>Weight Slip No.</th>
            <th>Time In</th>
            <th>Time Out</th>
            <th>Out Date</th>
            <th>Materials</th>
            <th>Remarks</th>
            {showCreatedBy && <th>Created By</th>}
            <th>Status</th>
            <th>Supervisor Action</th>
            {showActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {currentEntries.map((entry, idx) => {
            const supervisorAction = getSupervisorAction(entry);
            return (
              <tr key={(entry.id !== undefined && entry.id !== null) ? `${entry.id}-${idx}` : `row-${idx}`}>
                <td>{entry.serial_number}</td>
                <td>{entry.entry_date ? new Date(entry.entry_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                <td>{entry.party_name || '-'}</td>
                {renderBillsCell(entry.bills)}
                <td>{entry.entry_type || '-'}</td>
                <td>
                  {entry.transport_mode === 'By Hand'
                    ? (
                      <>
                        <div>By Hand</div>
                        <div className="text-muted">{entry.person_name || '-'}</div>
                        <div className="text-muted">{entry.mobile_number || '-'}</div>
                      </>
                    )
                    : entry.transport_mode === 'By Vehicle'
                    ? (
                      <>
                        <div>By Vehicle</div>
                        <div className="text-muted">{entry.vehicle_number || '-'}</div>
                        <div className="text-muted">{entry.vehicle_type || '-'}</div>
                      </>
                    )
                    : entry.transport_mode === 'By Courier'
                    ? (
                      <>
                        <div>By Courier</div>
                        <div className="text-muted">{entry.courier_name || '-'}</div>
                        <div className="text-muted">{entry.docket_number || '-'}</div>
                      </>
                    )
                    : '-'
                  }
                </td>
                <td>{entry.source_location || '-'}</td>
                {showReferenceColumn && (
                  <td>
                    {entry.entry_type === 'Challan' ? (entry.reference_number || '-') : ''}
                  </td>
                )}
                <td>{entry.po_number || '-'}</td>
                <td>{entry.gross_weight || '-'}</td>
                <td>{entry.tare_weight || '-'}</td>
                <td>{entry.net_weight || '-'}</td>
                <td>{entry.weight_slip_number || '-'}</td>
                <td>{formatTimeToLocal(entry.time_in) || '-'}</td>
                {renderTimeOutCell(entry, true)}
                <td>{entry.outdate ? new Date(entry.outdate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                {renderMaterialsCell(entry.materials_list)}
                <td>{entry.remarks || '-'}</td>
                {showCreatedBy && <td>{entry.created_by_name || '-'}</td>}
                <td>{getEntryStatus(entry)}</td>
                <td>
                  {supervisorAction
                    ? (
                      <span className="supervisor-action-badge" title={supervisorAction.remarks || ''}>
                        {supervisorAction.action}
                        {supervisorAction.remarks ? ` (${supervisorAction.remarks})` : ''}
                      </span>
                    )
                    : '-'
                  }
                </td>
                {showActions && (
                  <td>
                    {onEdit && (
                      <button
                        className="edit-button"
                        onClick={() => onEdit(entry)}
                        disabled={entry.completed || entry.time_out ? true : false}
                        title={entry.completed || entry.time_out ? "Can't edit completed entries" : "Edit entry"}
                      >
                        Edit
                      </button>
                    )}
                    {onMarkAction && (
                      <button
                        className="edit-button"
                        style={{
                          background: !!supervisorAction ? "#e0e0e0" : "#f59e42", // grey when disabled
                          color: !!supervisorAction ? "#888" : undefined,
                          marginLeft: 8,
                          cursor: !!supervisorAction ? "not-allowed" : "pointer"
                        }}
                        onClick={() => onMarkAction(entry, type)}
                        disabled={!!supervisorAction} // <-- Disable if supervisor action exists
                        title={!!supervisorAction ? "Action already marked" : "Mark Action"}
                      >
                        Mark Action
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {renderPagination()}
    </>
  );

  const renderOutwardTable = () => (
    <>
      <table>
        <thead>
          <tr>
            <th>Serial No.</th>
            <th>Date</th>
            <th>Transport Mode</th>
            <th>Transport Details</th>
            <th>Transport GR Number</th>
            <th>Source</th>
            <th>Time In</th>
            <th>Time Out</th>
            <th>Purpose</th>
            <th>Party Name</th>
            <th>Bill Details</th>
            <th>Gross Weight</th>
            <th>Tare Weight</th>
            <th>Net Weight</th>
            <th>Weight Slip No.</th>
            <th>Out Date</th>
            <th>Materials</th>
            <th>Remarks</th>
            {showCreatedBy && <th>Created By</th>}
            <th>Status</th>
            <th>Supervisor Action</th>
            {showActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {currentEntries.map((entry, idx) => {
            const supervisorAction = getSupervisorAction(entry);
            return (
              <tr key={(entry.id !== undefined && entry.id !== null) ? `${entry.id}-${idx}` : `row-${idx}`}>
                <td>{entry.serial_number}</td>
                <td>{entry.entry_date ? new Date(entry.entry_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                <td>{entry.transport_mode || '-'}</td>
                <td>
                  {entry.transport_mode === 'By Hand' && (
                    <>
                      <div>Person Name: {entry.person_name || '-'}</div>
                      <div>Person Mobile: {entry.person_mobile || '-'}</div>
                    </>
                  )}
                  {entry.transport_mode === 'By Vehicle' && (
                    <>
                      <div>Driver Name: {entry.driver_name || '-'}</div>
                      <div>Driver Mobile: {entry.driver_mobile || '-'}</div>
                      <div>Vehicle Number: {entry.vehicle_number || '-'}</div>
                      <div>Vehicle Type: {entry.vehicle_type || '-'}</div>
                    </>
                  )}
                  {entry.transport_mode === 'By Courier' && (
                    <>
                      <div>Courier Name: {entry.courier_name || '-'}</div>
                      <div>Docket Number: {entry.docket_number || '-'}</div>
                    </>
                  )}
                  {!['By Hand', 'By Vehicle', 'By Courier'].includes(entry.transport_mode) && (
                    <div>-</div>
                  )}
                </td>
                <td>{entry.transport_gr_number || '-'}</td>
                <td>{entry.source || '-'}</td>
                <td>{formatTimeToLocal(entry.time_in) || '-'}</td>
                {renderTimeOutCell(entry, false)}
                <td>{entry.purpose || '-'}</td>
                <td>{entry.party_name || '-'}</td>
                {renderBillsCell(entry.bills)}
                <td>{entry.gross_weight || '-'}</td>
                <td>{entry.tare_weight || '-'}</td>
                <td>{entry.net_weight || '-'}</td>
                <td>{entry.weight_slip_number || '-'}</td>
                <td>{entry.out_date ? new Date(entry.out_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                {renderMaterialsCell(entry.materials_list)}
                <td>{entry.remarks || '-'}</td>
                {showCreatedBy && <td>{entry.created_by_name || '-'}</td>}
                <td>{getEntryStatus(entry)}</td>
                <td>
                  {supervisorAction
                    ? (
                      <span className="supervisor-action-badge" title={supervisorAction.remarks || ''}>
                        {supervisorAction.action}
                        {supervisorAction.remarks ? ` (${supervisorAction.remarks})` : ''}
                      </span>
                    )
                    : '-'
                  }
                </td>
                {showActions && (
                  <td>
                    {onEdit && (
                      <button
                        className="edit-button"
                        onClick={() => onEdit(entry)}
                        disabled={entry.completed || entry.time_out ? true : false}
                        title={entry.completed || entry.time_out ? "Can't edit completed entries" : "Edit entry"}
                      >
                        Edit
                      </button>
                    )}
                    {onMarkAction && (
                      <button
                        className="edit-button"
                        style={{
                          background: !!supervisorAction ? "#e0e0e0" : "#f59e42", // grey when disabled
                          color: !!supervisorAction ? "#888" : undefined,
                          marginLeft: 8,
                          cursor: !!supervisorAction ? "not-allowed" : "pointer"
                        }}
                        onClick={() => onMarkAction(entry, type)}
                        disabled={!!supervisorAction} // <-- Disable if supervisor action exists
                        title={!!supervisorAction ? "Action already marked" : "Mark Action"}
                      >
                        Mark Action
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {renderPagination()}
    </>
  );

  const renderFilterSection = () => (
    <div className="table-filters">
      <div className="filters-header">
        <button 
          className="toggle-filters-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          <i className={`fas fa-filter`}></i>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          <i className={`fas fa-chevron-${showFilters ? 'up' : 'down'}`}></i>
        </button>
        <div className="quick-search">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            placeholder="Quick search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {showFilters && (
        <div className="filters-content">
          <div className="filters-grid">
            <div className="filter-item">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="filter-item">
              <label>Transport Mode</label>
              <select
                value={filters.transport_mode}
                onChange={(e) => setFilters(prev => ({ ...prev, transport_mode: e.target.value }))}
              >
                <option value="">All Modes</option>
                {[...new Set(entries.map(e => e.transport_mode))].filter(Boolean).map(mode => (
                  <option key={mode} value={mode}>{mode}</option>
                ))}
              </select>
            </div>

            {/* Entry Type filter: show only for inward */}
            {type === 'inward' && (
              <div className="filter-item">
                <label>Entry Type</label>
                <select
                  value={filters.entry_type}
                  onChange={(e) => setFilters(prev => ({ ...prev, entry_type: e.target.value }))}
                >
                  <option value="">All Types</option>
                  {[...new Set(entries.map(e => e.entry_type))].filter(Boolean).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Purpose filter: show only for outward */}
            {type === 'outward' && (
              <div className="filter-item">
                <label>Purpose</label>
                <select
                  value={filters.purpose}
                  onChange={e => setFilters(prev => ({ ...prev, purpose: e.target.value }))}
                >
                  <option value="">All Purposes</option>
                  {[...new Set(entries.map(e => e.purpose))].filter(Boolean).map(purpose => (
                    <option key={purpose} value={purpose}>{purpose}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-item">
              <label>From Date</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              />
            </div>

            <div className="filter-item">
              <label>To Date</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              />
            </div>
          </div>

          <div className="filters-footer">
            <button
              className="clear-filters-button"
              onClick={() => {
                setSearchTerm('');
                setFilters({
                  status: 'all',
                  transport_mode: '', // changed from vehicle_type
                  entry_type: '', // reset entry_type filter
                  purpose: '', // reset purpose filter
                  date_from: '',
                  date_to: '',
                });
              }}
            >
              <i className="fas fa-times"></i>
              Clear All Filters
            </button>
            <div className="results-count">
              <i className="fas fa-table"></i>
              {filteredEntries.length} entries found
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="entry-history-table">
      {renderFilterSection()}
      {type === 'inward' ? renderInwardTable() : renderOutwardTable()}
    </div>
  );
};

export default EntryHistoryTable;
